import "../css/header.scss";
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";

const readWishlistCount = () => {
  if (typeof window === "undefined") return 0;

  try {
    const savedWishlist =
      window.localStorage.getItem("wishlistItems") ||
      window.localStorage.getItem("wishlist");

    if (!savedWishlist) return 0;

    const parsedWishlist = JSON.parse(savedWishlist);
    if (Array.isArray(parsedWishlist)) return parsedWishlist.length;
    if (parsedWishlist && typeof parsedWishlist === "object") {
      return Object.keys(parsedWishlist).length;
    }
  } catch (error) {
    return 0;
  }

  return 0;
};

const SearchIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    width="20"
    height="20"
  >
    <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" />
    <path d="m16.5 16.5 4 4" fill="none" stroke="currentColor" />
  </svg>
);

const HeartIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    width="20"
    height="20"
  >
    <path
      d="M20.8 5.8a5.4 5.4 0 0 0-7.6 0L12 7l-1.2-1.2a5.4 5.4 0 0 0-7.6 7.6L12 22l8.8-8.6a5.4 5.4 0 0 0 0-7.6Z"
      fill="none"
      stroke="currentColor"
    />
  </svg>
);

const BagIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    width="20"
    height="20"
  >
    <path d="M4 8h16l-1 13H5L4 8Z" fill="none" stroke="currentColor" />
    <path d="M8.5 9V6a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="currentColor" />
  </svg>
);

function Header() {
  const { cartItems = [], wishlistCount: contextWishlistCount } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileShopOpen, setIsMobileShopOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [storedWishlistCount, setStoredWishlistCount] = useState(readWishlistCount);
  const wishlistCount = Number.isFinite(Number(contextWishlistCount))
    ? Number(contextWishlistCount)
    : storedWishlistCount;

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(siteConfig.locale, {
        style: "currency",
        currency: siteConfig.currency,
      }),
    []
  );

  const cartSummary = useMemo(
    () =>
      cartItems.reduce(
        (summary, cartItem) => {
          const quantity = Number(cartItem.quantity) || 0;
          const product = productData.find(
            (item) => Number(item.id) === Number(cartItem.id)
          );

          return {
            count: summary.count + quantity,
            amount:
              summary.amount + (product ? Number(product.price) * quantity : 0),
          };
        },
        { count: 0, amount: 0 }
      ),
    [cartItems]
  );

  const searchResults = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase(siteConfig.locale);
    if (query.length < 2) return [];

    return productData
      .filter((product) => {
        const searchableText = [
          product.name,
          product.category,
          product.artist,
          ...(product.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase(siteConfig.locale);

        return searchableText.includes(query);
      })
      .slice(0, 5);
  }, [searchTerm]);

  const shopLinks = siteConfig.navigation.shop;
  const themeLinks = siteConfig.navigation.header.filter(
    (item) => item.path !== "/shop" && item.path !== "/about"
  );
  const aboutLink =
    siteConfig.navigation.company.find((item) => item.path === "/about") ||
    siteConfig.navigation.company[0];
  const createLink = {
    label: "Create",
    path: "/contact?subject=custom-notebooks",
  };

  useEffect(() => {
    setIsSearchOpen(false);
    setIsShopOpen(false);
    setIsMoreOpen(false);
    setIsMobileOpen(false);
    setIsMobileShopOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setIsSearchOpen(false);
      setIsShopOpen(false);
      setIsMoreOpen(false);
      setIsMobileOpen(false);
      setIsMobileShopOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  useEffect(() => {
    if (!isSearchOpen) return undefined;

    const focusTimer = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(focusTimer);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isMobileOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileOpen]);

  useEffect(() => {
    const refreshWishlistCount = () => setStoredWishlistCount(readWishlistCount());

    window.addEventListener("storage", refreshWishlistCount);
    window.addEventListener("wishlist:updated", refreshWishlistCount);
    window.addEventListener("focus", refreshWishlistCount);

    return () => {
      window.removeEventListener("storage", refreshWishlistCount);
      window.removeEventListener("wishlist:updated", refreshWishlistCount);
      window.removeEventListener("focus", refreshWishlistCount);
    };
  }, []);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavigation = () => {
    setIsSearchOpen(false);
    setIsShopOpen(false);
    setIsMoreOpen(false);
    setIsMobileOpen(false);
    setIsMobileShopOpen(false);
    toTop();
  };

  const openSearch = () => {
    setIsSearchOpen(true);
    setIsShopOpen(false);
    setIsMoreOpen(false);
    setIsMobileOpen(false);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    if (!query) {
      searchInputRef.current?.focus();
      return;
    }

    navigate(`/shop?q=${encodeURIComponent(query)}`);
    setIsSearchOpen(false);
    setSearchTerm("");
    toTop();
  };

  const handleMobileToggle = () => {
    setIsMobileOpen((isOpen) => !isOpen);
    setIsSearchOpen(false);
    setIsShopOpen(false);
  };

  return (
    <>
      <header className="site-header">
        <div className="announcement-bar">
          <p className="header-announcement-text">
            Tracked worldwide shipping {currencyFormatter.format(siteConfig.shipping.standardRate)}
          </p>
          <Link
            className="header-announcement-link"
            to="/about"
            onClick={handleNavigation}
          >
            Art can change the world. See our story
          </Link>
        </div>

        <div className="header-shell">
          <button
            className={`header-menu-toggle${
              isMobileOpen ? " header-menu-toggle--open" : ""
            }`}
            type="button"
            aria-expanded={isMobileOpen}
            aria-controls="header-mobile-drawer"
            aria-label={isMobileOpen ? "Close navigation" : "Open navigation"}
            onClick={handleMobileToggle}
          >
            <span className="header-menu-line" />
            <span className="header-menu-line" />
            <span className="header-menu-line" />
          </button>

          <nav className="header-navigation" aria-label="Primary navigation">
            <div className="header-navigation-item">
              <button
                className="header-navigation-link header-navigation-button"
                type="button"
                aria-expanded={isShopOpen}
                aria-controls="header-shop-menu"
                onClick={() => setIsShopOpen((isOpen) => !isOpen)}
              >
                Shop
                <span className="header-navigation-arrow" aria-hidden="true">
                  ↓
                </span>
              </button>
            </div>

            <Link
              className="header-navigation-link header-navigation-create"
              to={createLink.path}
              onClick={handleNavigation}
            >
              {createLink.label}
            </Link>

            <Link
              className="header-navigation-link"
              to={aboutLink.path}
              onClick={handleNavigation}
            >
              {aboutLink.label}
            </Link>
            <div className="header-navigation-item">
              <button className="header-navigation-link header-navigation-button" type="button" aria-expanded={isMoreOpen} aria-controls="header-more-menu" onClick={() => { setIsMoreOpen((open) => !open); setIsShopOpen(false); }}>
                More <span className="header-navigation-arrow" aria-hidden="true">↓</span>
              </button>
            </div>
          </nav>

          <Link
            className="header-logo"
            to="/"
            aria-label={`${siteConfig.brandName} home`}
            onClick={handleNavigation}
          >
            {siteConfig.logo.text || siteConfig.logoText}
          </Link>

          <div className="header-actions">
            <button
              className="header-action header-search-trigger"
              type="button"
              aria-expanded={isSearchOpen}
              aria-controls="header-search-panel"
              aria-label="Search products"
              onClick={openSearch}
            >
              <SearchIcon />
              <span className="header-action-label">Search</span>
            </button>

            <Link
              className="header-action header-wishlist-link"
              to="/shop?wishlist=true"
              aria-label={`Wishlist with ${wishlistCount} items`}
            >
              <HeartIcon />
              <span className="header-action-label">Wishlist</span>
              <span className="header-action-count" aria-hidden="true">
                {wishlistCount}
              </span>
            </Link>

            <Link
              className="header-action header-cart-link"
              to="/cartpage"
              aria-label={`Cart with ${cartSummary.count} items, ${currencyFormatter.format(
                cartSummary.amount
              )}`}
            >
              <BagIcon />
              <span className="header-cart-copy">
                <span className="header-action-label">Cart</span>
                <span className="header-cart-amount">
                  {currencyFormatter.format(cartSummary.amount)}
                </span>
              </span>
              <span className="header-action-count" aria-hidden="true">
                {cartSummary.count}
              </span>
            </Link>
          </div>
        </div>

        {isShopOpen && (
          <div className="header-mega-menu" id="header-shop-menu">
            <div className="header-mega-inner">
              <section className="header-mega-section" aria-labelledby="header-shop-heading">
                <p className="header-mega-heading" id="header-shop-heading">
                  Shop notebooks
                </p>
                <div className="header-mega-links">
                  {shopLinks.map((item) => (
                    <Link
                      className="header-mega-link"
                      key={item.path}
                      to={item.path}
                      onClick={handleNavigation}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </section>

              <section className="header-mega-section" aria-labelledby="header-theme-heading">
                <p className="header-mega-heading" id="header-theme-heading">
                  Shop by theme
                </p>
                <div className="header-mega-links">
                  {themeLinks.map((item) => (
                    <Link
                      className="header-mega-link"
                      key={`${item.label}-${item.path}`}
                      to={item.path}
                      onClick={handleNavigation}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </section>

              <Link
                className="header-mega-callout"
                to={createLink.path}
                onClick={handleNavigation}
              >
                <span className="header-mega-eyebrow">Make it yours</span>
                <strong className="header-mega-title">Create custom notebooks</strong>
                <span className="header-mega-copy">
                  Designed for teams, shops, schools, and meaningful events.
                </span>
                <span className="header-mega-cta">Start a project →</span>
              </Link>
            </div>
          </div>
        )}

        {isMoreOpen && (
          <div className="header-mega-menu" id="header-more-menu">
            <div className="header-mega-inner header-mega-inner--more">
              <section className="header-mega-section" aria-labelledby="header-more-heading">
                <p className="header-mega-heading" id="header-more-heading">Policies & help</p>
                <div className="header-mega-links">
                  {siteConfig.navigation.help.map((item) => <Link className="header-mega-link" key={`${item.label}-${item.path}`} to={item.path} onClick={handleNavigation}>{item.label}</Link>)}
                </div>
              </section>
            </div>
          </div>
        )}

        {isSearchOpen && (
          <section
            className="header-search-panel"
            id="header-search-panel"
            aria-label="Product search"
          >
            <div className="header-search-inner">
              <form
                className="header-search-form"
                id="header-search-form"
                onSubmit={handleSearchSubmit}
              >
                <label className="header-search-label" htmlFor="header-search-input">
                  Search the collection
                </label>
                <div className="header-search-field">
                  <SearchIcon />
                  <input
                    className="header-search-input"
                    id="header-search-input"
                    ref={searchInputRef}
                    type="search"
                    value={searchTerm}
                    placeholder="Try ‘layflat’, ‘floral’, or an artist"
                    autoComplete="off"
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <button className="header-search-submit" type="submit">
                    Search
                  </button>
                </div>
              </form>

              <button
                className="header-search-close"
                type="button"
                aria-label="Close search"
                onClick={closeSearch}
              >
                ×
              </button>

              {searchTerm.trim().length >= 2 && (
                <div className="header-search-results" aria-live="polite">
                  {searchResults.length > 0 ? (
                    <>
                      <p className="header-search-results-heading">
                        Suggested products
                      </p>
                      <div className="header-search-results-list">
                        {searchResults.map((product) => (
                          <Link
                            className="header-search-result"
                            key={product.id}
                            to={`/shopDetails/${product.id}`}
                            onClick={handleNavigation}
                          >
                            <img
                              className="header-search-result-image"
                              src={product.images[0]}
                              alt=""
                              width="72"
                              height="88"
                            />
                            <span className="header-search-result-copy">
                              <span className="header-search-result-category">
                                {product.category}
                              </span>
                              <strong className="header-search-result-name">
                                {product.name}
                              </strong>
                              <span className="header-search-result-price">
                                {currencyFormatter.format(Number(product.price))}
                              </span>
                            </span>
                          </Link>
                        ))}
                      </div>
                      <button className="header-search-all" type="submit" form="header-search-form">
                        View all results for “{searchTerm.trim()}”
                      </button>
                    </>
                  ) : (
                    <p className="header-search-empty">
                      No products match “{searchTerm.trim()}”. Try another word.
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
      </header>

      {isMobileOpen && (
        <div className="header-drawer" id="header-mobile-drawer">
          <button
            className="header-drawer-backdrop"
            type="button"
            aria-label="Close navigation"
            onClick={() => setIsMobileOpen(false)}
          />
          <aside className="header-drawer-panel" aria-label="Mobile navigation">
            <div className="header-drawer-top">
              <Link
                className="header-drawer-logo"
                to="/"
                aria-label={`${siteConfig.brandName} home`}
                onClick={handleNavigation}
              >
                {siteConfig.logo.text || siteConfig.logoText}
              </Link>
              <button
                className="header-drawer-close"
                type="button"
                aria-label="Close navigation"
                onClick={() => setIsMobileOpen(false)}
              >
                ×
              </button>
            </div>

            <nav className="header-drawer-navigation" aria-label="Mobile primary navigation">
              <button
                className="header-drawer-link header-drawer-shop-toggle"
                type="button"
                aria-expanded={isMobileShopOpen}
                aria-controls="header-mobile-shop-links"
                onClick={() => setIsMobileShopOpen((isOpen) => !isOpen)}
              >
                Shop
                <span className="header-drawer-symbol" aria-hidden="true">
                  {isMobileShopOpen ? "−" : "+"}
                </span>
              </button>

              {isMobileShopOpen && (
                <div className="header-drawer-subnav" id="header-mobile-shop-links">
                  {shopLinks.map((item) => (
                    <Link
                      className="header-drawer-sublink"
                      key={item.path}
                      to={item.path}
                      onClick={handleNavigation}
                    >
                      {item.label}
                    </Link>
                  ))}
                  {themeLinks.map((item) => (
                    <Link
                      className="header-drawer-sublink"
                      key={`${item.label}-${item.path}`}
                      to={item.path}
                      onClick={handleNavigation}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}

              <Link
                className="header-drawer-link"
                to={createLink.path}
                onClick={handleNavigation}
              >
                {createLink.label}
              </Link>
              <Link
                className="header-drawer-link"
                to={aboutLink.path}
                onClick={handleNavigation}
              >
                {aboutLink.label}
              </Link>
              {siteConfig.navigation.help.map((item) => <Link className="header-drawer-link" key={`mobile-${item.label}`} to={item.path} onClick={handleNavigation}>{item.label}</Link>)}
              <button className="header-drawer-link" type="button" onClick={openSearch}>
                Search
              </button>
            </nav>

            <div className="header-drawer-footer">
              <Link className="header-drawer-utility" to="/shop?wishlist=true">
                Wishlist ({wishlistCount})
              </Link>
              <Link className="header-drawer-utility" to="/cartpage">
                Cart ({cartSummary.count}) · {currencyFormatter.format(cartSummary.amount)}
              </Link>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

export default Header;
